import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AuthErrorScreen, AuthLoadingScreen } from '@/auth/AuthErrorScreen'
import { useAuth } from '@/auth/AuthContext'
import { mapFirebaseAuthError } from '@/auth/errors'
import { signUpWithEmail } from '@/auth/firebase'
import { consumeAuthRedirectPath } from '@/auth/storage'
import { useTranslation } from '@/i18n'

export function RegisterPage() {
  const navigate = useNavigate()
  const { authState, loading } = useAuth()
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (loading) return

    if (authState === 'ready') {
      navigate(consumeAuthRedirectPath('/dashboard'), { replace: true })
      return
    }

    if (authState === 'verifyEmail') {
      navigate('/verifieer-email', { replace: true })
      return
    }

    if (authState === 'needsProfile') {
      navigate('/account-aanmaken', { replace: true })
    }
  }, [authState, loading, navigate])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      await signUpWithEmail(email, password)
      navigate('/verifieer-email', { replace: true })
    } catch (submitError) {
      setError(t(mapFirebaseAuthError(submitError, 'signup')))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <AuthLoadingScreen fullscreen={false} />
  if (authState === 'error') return <AuthErrorScreen fullscreen={false} />
  if (authState !== 'notLoggedIn') return <AuthLoadingScreen fullscreen={false} />

  return (
    <div className="mx-auto w-full max-w-md">
      <h2 className="mb-2 text-xl font-semibold text-surface-100">{t('Account aanmaken')}</h2>
      <p className="mb-6 text-sm text-surface-400">
        {t('Na signup sturen we eerst een verificatiemail. Daarna beslist de auth-gate of je door kunt naar profiel-aanmaak of direct de app in.')}
      </p>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div>
          <label htmlFor="register-email" className="mb-1.5 block text-sm text-surface-300">
            {t('E-mailadres')}
          </label>
          <input
            id="register-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={submitting}
            className="w-full rounded-xl border border-surface-700 bg-surface-800 px-4 py-3 text-sm text-surface-100 placeholder-surface-500 transition focus:border-accent-500 focus:outline-none disabled:opacity-50"
            placeholder={t('naam@voorbeeld.nl')}
          />
        </div>

        <div>
          <label htmlFor="register-password" className="mb-1.5 block text-sm text-surface-300">
            {t('Wachtwoord')}
          </label>
          <input
            id="register-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={submitting}
            className="w-full rounded-xl border border-surface-700 bg-surface-800 px-4 py-3 text-sm text-surface-100 placeholder-surface-500 transition focus:border-accent-500 focus:outline-none disabled:opacity-50"
            placeholder={t('Minimaal 6 tekens')}
          />
        </div>

        {error ? (
          <p className="rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">{error}</p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || !email.trim() || password.length < 6}
          className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent-300 border-t-white" />
          ) : null}
          {t('Account aanmaken')}
        </button>
      </form>

      <div className="mt-6 border-t border-surface-800 pt-5 text-center text-sm text-surface-400">
        {t('Heb je al een account?')}{' '}
        <Link to="/login" className="font-medium text-accent-400 hover:text-accent-300">
          {t('Log dan in')}
        </Link>
      </div>
    </div>
  )
}
